<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL } from 'dashboard/helper/URLHelper';
import segueDashboardAPI from 'dashboard/api/segueDashboard';

const props = defineProps({
  phone: { type: String, default: '' },
  nome: { type: String, default: '' },
});
// v93: 'navigate' avisa quem abriu pra fechar as janelas em cascata
const emit = defineEmits(['close', 'navigate']);

const router = useRouter();
const route = useRoute();
const loading = ref(true);
const found = ref(false);
const contactName = ref('');
const messages = ref([]);
const errorMsg = ref('');
const conversationId = ref(null);

// v93: botão verde ABRIR CONVERSA — redireciona e fecha as janelas
const abrirConversa = () => {
  if (!conversationId.value) return;
  const accountId = route.params.accountId || 1;
  router.push(
    frontendURL(`accounts/${accountId}/conversations/${conversationId.value}`)
  );
  emit('navigate');
  emit('close');
};

const fmtTime = iso => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (e) {
    return '';
  }
};

const load = async () => {
  loading.value = true;
  errorMsg.value = '';
  try {
    const { data } = await segueDashboardAPI.fetchConversa(props.phone);
    found.value = !!data.found;
    contactName.value = data.contact_name || props.nome || '';
    messages.value = data.messages || [];
    conversationId.value = data.conversation_id || null;
    if (!data.found) {
      errorMsg.value = 'Nenhuma conversa encontrada para este telefone.';
    }
  } catch (e) {
    errorMsg.value = 'Erro ao carregar a conversa.';
  } finally {
    loading.value = false;
  }
};

onMounted(load);
</script>

<!-- eslint-disable vue/no-bare-strings-in-template -->
<template>
  <div
    class="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black/50"
    @click.self="emit('close')"
  >
    <div
      class="flex flex-col w-full max-w-lg h-[80vh] rounded-2xl shadow-xl bg-n-background overflow-hidden"
    >
      <!-- Header -->
      <header
        class="flex flex-shrink-0 gap-3 justify-between items-center px-4 py-3 border-b border-n-weak"
      >
        <div class="min-w-0">
          <p class="font-semibold truncate text-n-slate-12">
            {{ contactName || 'Conversa' }}
          </p>
          <p class="text-xs truncate text-n-slate-11">
            {{ props.phone }} · somente leitura
          </p>
        </div>
        <div class="flex flex-shrink-0 gap-2 items-center">
          <!-- v93: abrir a conversa de verdade (fecha as janelas) -->
          <button
            v-if="conversationId"
            type="button"
            class="px-3 py-1.5 text-xs font-bold text-white rounded-lg bg-n-teal-9 hover:opacity-90"
            @click="abrirConversa"
          >
            ABRIR CONVERSA
          </button>
          <button
            type="button"
            class="flex flex-shrink-0 justify-center items-center text-white bg-red-500 rounded-lg size-8 hover:bg-red-600"
            title="Fechar"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-5" />
          </button>
        </div>
      </header>

      <!-- Corpo -->
      <div class="overflow-y-auto flex-1 px-4 py-3 space-y-2 bg-n-alpha-1">
        <p v-if="loading" class="py-8 text-sm text-center text-n-slate-11">
          Carregando conversa…
        </p>
        <p
          v-else-if="errorMsg"
          class="py-8 text-sm text-center text-n-slate-11"
        >
          {{ errorMsg }}
        </p>
        <template v-else>
          <div
            v-for="msg in messages"
            :key="msg.id"
            class="flex"
            :class="msg.outgoing ? 'justify-end' : 'justify-start'"
          >
            <div
              class="px-3 py-2 max-w-[78%] rounded-2xl text-sm shadow-sm"
              :class="
                msg.outgoing
                  ? 'bg-green-800 text-green-50 rounded-br-sm'
                  : 'bg-n-solid-2 text-n-slate-12 rounded-bl-sm'
              "
            >
              <p
                v-if="msg.outgoing && msg.sender"
                class="mb-0.5 text-[11px] font-medium opacity-70"
              >
                {{ msg.sender }}
              </p>
              <p v-if="msg.content" class="whitespace-pre-wrap break-words">
                {{ msg.content }}
              </p>
              <div
                v-for="(att, i) in msg.attachments"
                :key="i"
                class="mt-1"
              >
                <img
                  v-if="att.type === 'image' && att.url"
                  :src="att.url"
                  class="max-h-48 rounded-lg"
                />
                <a
                  v-else-if="att.url"
                  :href="att.url"
                  target="_blank"
                  rel="noopener"
                  class="text-xs underline"
                >
                  [anexo: {{ att.type }}]
                </a>
                <span v-else class="text-xs opacity-70">
                  [anexo: {{ att.type }}]
                </span>
              </div>
              <p class="mt-0.5 text-[10px] text-right opacity-60">
                {{ fmtTime(msg.created_at) }}
              </p>
            </div>
          </div>
          <p
            v-if="!messages.length"
            class="py-8 text-sm text-center text-n-slate-11"
          >
            Sem mensagens nesta conversa.
          </p>
        </template>
      </div>

      <footer
        class="flex-shrink-0 px-4 py-2 text-[11px] text-center border-t border-n-weak text-n-slate-10"
      >
        Visualização somente leitura — mensagens até o momento da abertura.
      </footer>
    </div>
  </div>
</template>
